// extra data that is used on blog category pages for SEO purposes
// ids are category names that match on Sanity and should not be changed
// all other properties are hardcoded and can be changed according to requirements

import type {
  BlogCategory,
  BlogCategoryExtraData,
} from '@bulknutrients/bulk-shared-lib/interfaces/Blog';
import {
  getBlogCategoryNames,
  getBlogData,
} from '@bulknutrients/bulk-shared-lib/lib/sanity/blogData';
import { getRedirect } from '@bulknutrients/bulk-shared-lib/lib/sanity/redirect';
import { BlogCardList } from '@components/Blog/BlogCardList';
import { BlogCategoryNavBar } from '@components/Blog/BlogCategoryNavBar';
import { BlogIntro } from '@components/Blog/BlogIntro';
import { Breadcrumbs } from '@components/Breadcrumbs';
import { Container } from '@components/Container';
import { StandardLayout } from '@components/Layouts';
import blogCategoriesExtraDataJson from '@data/blogCategoriesExtraData';
import type { GetStaticPaths, GetStaticProps } from 'next';
import Head from 'next/head';
import type { ReactElement } from 'react';

export default function BlogCategoryLandingPage({
  slug,
  category,
  blogCategories,
  blogCategoryExtraData,
  breadcrumbs,
}: {
  slug: string;
  category: string;
  blogCategories: BlogCategory[];
  blogCategoryExtraData?: BlogCategoryExtraData;
  breadcrumbs: { title: string; path: string }[];
}): ReactElement {
  const metaTitle = blogCategoryExtraData
    ? blogCategoryExtraData.title
    : `${category} blogs on Bulk Nutrients`;
  return (
    <>
      <Head>
        <title>{metaTitle}</title>
        <meta name="title" content={metaTitle} />
        <meta
          name="description"
          content={
            blogCategoryExtraData
              ? blogCategoryExtraData.metaDescription
              : `${category} blogs on Bulk Nutrients`
          }
        />
        <link
          rel="canonical"
          href={`${process.env.NEXT_PUBLIC_SERVER_URL}${slug}`}
        />
      </Head>
      <Container>
        <Breadcrumbs crumbs={breadcrumbs} />
      </Container>
      <Container as="main" id="main-content">
        <BlogIntro
          category={category}
          blogCategoryExtraData={blogCategoryExtraData}
        />
        <BlogCategoryNavBar blogCategories={blogCategories} />
        <BlogCardList category={category} />
      </Container>
    </>
  );
}

BlogCategoryLandingPage.getLayout = function getLayout(page: ReactElement) {
  return <StandardLayout>{page}</StandardLayout>;
};

export const getStaticPaths: GetStaticPaths = async () => {
  //   Get all the categories (title and slug)
  const blogCategories = await getBlogCategoryNames();
  // Turn them into param paths
  const paths = blogCategories
    .map((c) => c.slug.slice(6))
    .map((slug) => ({
      params: {
        category: slug,
      },
    }));

  return {
    paths,
    fallback: 'blocking',
  };
};

export const getStaticProps: GetStaticProps = async ({ params }) => {
  //   Get all categories, and then pull out title of current one to filter the cards
  const blogCategories = await getBlogCategoryNames();
  const category = blogCategories.filter(
    (c) => c.slug.slice(6) === params?.category?.toString()
  )[0]?.category;
  const slug =
    blogCategories.filter((b) => b.category === category)[0]?.slug ?? '';

  if (!category) {
    // Check if it is something to redirect
    const blog = await getBlogData(params?.category?.toString() as string);
    if (blog) {
      return {
        redirect: {
          destination: blog.slug,
          permanent: false,
        },
      };
    }

    // try redirect
    const redirect = await getRedirect(
      `/blog/${params?.category?.toString()}` as string
    );
    if (redirect) {
      return {
        redirect: {
          destination: redirect.newUrl,
          permanent: redirect.permanent,
        },
      };
    }

    // fallback failed
    return {
      notFound: true,
    };
  }

  const breadcrumbs = [
    { title: 'home', path: '/' },
    { title: 'The Bulk Nutrients Blog', path: '/blog/' },
  ];

  const blogCategoriesExtraData: BlogCategoryExtraData[] =
    blogCategoriesExtraDataJson;

  const blogCategoryExtraData =
    blogCategoriesExtraData.find((c) => c.id === category) || null;

  return {
    props: {
      slug,
      category,
      blogCategories,
      blogCategoryExtraData,
      breadcrumbs,
    },
  };
};
